import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Spin, Form, Input, Select, Space, Typography,
  DatePicker, Popconfirm, Row, Col,
} from 'antd';
import {
  EditOutlined, SaveOutlined, DeleteOutlined, ArrowLeftOutlined,
  UserOutlined, BankOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { useStore } from '../../stores/root-store-context';
import ERPLayout from '../../components/layout/ERPLayout';
import { CrmEventDto } from '../../api/CRMApiClient';

dayjs.extend(utc); dayjs.extend(timezone);
const toLocal = (iso: string) => dayjs(iso).local();
const toApi   = (d: Dayjs) => d.utc().format('YYYY-MM-DDTHH:mm:ss');

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const EventDetail: React.FC = observer(() => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const { crmStore, applicationStore } = useStore();

  const isNew = !eventId || eventId === 'new';

  const [form] = Form.useForm();
  const [editing, setEditing] = useState(isNew);
  const [loading, setLoading] = useState(!isNew);

  const buildCompositeName = useCallback(
    (companyIds?: number[], contactIds?: number[], range?: [Dayjs, Dayjs]) => {
      const parts: string[] = [];
      if (companyIds?.length) {
        const first = crmStore.externalCompanies.find(c => c.id === companyIds[0]);
        if (first) parts.push(first.name);
        if (companyIds.length > 1) parts.push(`+${companyIds.length - 1}`);
      }
      if (contactIds?.length) {
        const first = crmStore.contacts.find(c => c.id === contactIds[0]);
        if (first) parts.push(`${first.name} ${first.surname}`.trim());
        if (contactIds.length > 1) parts.push(`+${contactIds.length - 1}`);
      }
      if (range?.[0]) parts.push(range[0].format('DD/MM/YYYY'));
      return parts.filter(Boolean).join(' — ');
    },
    [crmStore.externalCompanies, crmStore.contacts],
  );

  useEffect(() => {
    (async () => {
      await Promise.all([
        crmStore.getCrmEventTypes(),
        crmStore.getContactsWithDetails(),
        crmStore.getExternalCompanies(),
      ]);

      if (!isNew && eventId) {
        const id = Number(eventId);
        const evt = await crmStore.getCrmEventById(id);
        if (evt) {
          const dateRange: [Dayjs, Dayjs] = [toLocal(evt.dateStart), toLocal(evt.dateEnd)];
          form.setFieldsValue({
            ...evt,
            externalCompanyIds: evt.externalCompanies?.map(c => c.id) ?? [],
            contactIds: evt.contacts?.map(c => c.id) ?? [],
            dateRange,
            name: buildCompositeName(
              evt.externalCompanies?.map(c => c.id),
              evt.contacts?.map(c => c.id),
              dateRange,
            ),
          });
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [eventId]);

  const handleValuesChange = (_: any, all: any) => {
    if (!editing) return;
    const composite = buildCompositeName(all.externalCompanyIds, all.contactIds, all.dateRange);
    if (composite && composite !== all.name) form.setFieldsValue({ name: composite });
  };

  const onSave = async () => {
    const { dateRange, externalCompanyIds, contactIds, ...rest } =
      await form.validateFields();
    const payload: CrmEventDto = {
      ...rest,
      dateStart: toApi(dateRange[0]),
      dateEnd:   toApi(dateRange[1]),
      externalCompanyIds,
      contactIds,
      name: buildCompositeName(externalCompanyIds, contactIds, dateRange),
    };
    if (isNew) {
      const id = await crmStore.addCrmEvent(payload);
      if (id) navigate(`/${applicationStore.company?.Tenant}/crm/event/${id}`);
    } else if (eventId) {
      await crmStore.updateCrmEvent(Number(eventId), payload);
      setEditing(false);
    }
  };

  const onDelete = async () => {
    if (isNew || !eventId) return;
    await crmStore.deleteCrmEvent(Number(eventId));
    navigate(`/${applicationStore.company?.Tenant}/crm/events`);
  };

  const { firstContact, firstCompany } = useMemo(() => {
    const ev = crmStore.currentCrmEvent;
    return {
      firstContact: ev?.contacts?.[0] ?? null,
      firstCompany: ev?.externalCompanies?.[0] ?? null,
    };
  }, [crmStore.currentCrmEvent]);

  const tenantPath = `/${applicationStore.company?.Tenant}`;
  const breadcrumbItems = [
    { label: t('Home'),   path: tenantPath },
    { label: 'CRM',       path: `${tenantPath}/crm` },
    { label: t('Events'), path: `${tenantPath}/crm/events` },
    {
      label: isNew ? t('AddCrmEvent') : t('EventDetails'),
      path : isNew ? `${tenantPath}/crm/event/new`
                   : `${tenantPath}/crm/event/${eventId}`,
    },
  ];

  return (
    <ERPLayout breadcrumbTitle={t('EventDetails')} breadcrumbItems={breadcrumbItems}>
      <Spin spinning={loading || crmStore.isLoading}>
        <Card
          title={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`${tenantPath}/crm/events`)} />
              <Title level={4} style={{ margin: 0 }}>
                {isNew
                  ? t('AddCrmEvent')
                  : crmStore.currentCrmEvent?.name || t('EventDetails')}
              </Title>
            </Space>
          }
          extra={
            <Space>
              {editing ? (
                <>
                  <Button type="primary" icon={<SaveOutlined />} onClick={onSave}>
                    {t('Save')}
                  </Button>
                  {!isNew && <Button onClick={() => setEditing(false)}>{t('Cancel')}</Button>}
                </>
              ) : (
                <Button type="primary" icon={<EditOutlined />} onClick={() => setEditing(true)}>
                  {t('Edit')}
                </Button>
              )}
              {!isNew && (
                <Popconfirm title={t('DeleteEventConfirm')} onConfirm={onDelete}>
                  <Button danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
                </Popconfirm>
              )}
            </Space>
          }
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form
                form={form}
                layout="vertical"
                disabled={!editing}
                onValuesChange={handleValuesChange}
              >
                <Form.Item name="externalCompanyIds" label={t('Companies')}>
                  <Select mode="multiple" allowClear showSearch optionFilterProp="children">
                    {crmStore.externalCompanies.map(c => (
                      <Option key={c.id} value={c.id}>{c.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="contactIds" label={t('Contacts')}>
                  <Select mode="multiple" allowClear showSearch optionFilterProp="children">
                    {crmStore.contacts.map(c => (
                      <Option key={c.id} value={c.id}>{c.name} {c.surname}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="dateRange"
                  label={t('DateTime')}
                  rules={[{ required: true, message: t('PleaseSelectDateTime') }]}
                >
                  <RangePicker
                    showTime
                    allowClear={false}
                    format="DD/MM/YYYY HH:mm"
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item
                  name="typeId"
                  label={t('EventType')}
                  rules={[{ required: true, message: t('PleaseSelectEventType') }]}
                >
                  <Select placeholder={t('Select')} allowClear>
                    {crmStore.CrmEventTypes.map(et => (
                      <Option key={et.id} value={et.id}>{et.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}>
                  <Input disabled />
                </Form.Item>

                <Form.Item name="description" label={t('Description')}>
                  <TextArea rows={4} />
                </Form.Item>
              </Form>
            </Col>

            <Col xs={24} md={12}>
              {!editing && !isNew && (
                <>
                  <Title level={5}>{t('RelatedEntities')}</Title>

                  {firstContact && (
                    <Card size="small" style={{ marginBottom: 16 }}
                      title={<Space><UserOutlined />{t('Contact')}</Space>}
                      extra={
                        <Button type="link"
                          onClick={() => navigate(`${tenantPath}/crm/contact/${firstContact.id}`)}
                        >
                          {t('View')}
                        </Button>
                      }
                    >
                      <p><strong>{t('Name')}:</strong> {firstContact.name} {firstContact.surname}</p>
                      {firstContact.email && <p><strong>{t('Email')}:</strong> {firstContact.email}</p>}
                      {firstContact.phoneNumber && <p><strong>{t('Phone')}:</strong> {firstContact.phoneNumber}</p>}
                      {crmStore.currentCrmEvent?.contacts?.length! > 1 && (
                        <Text type="secondary">
                          {t('AndOther', { count: crmStore.currentCrmEvent!.contacts!.length - 1 })}
                        </Text>
                      )}
                    </Card>
                  )}

                  {firstCompany && (
                    <Card size="small" style={{ marginBottom: 16 }}
                      title={<Space><BankOutlined />{t('Company')}</Space>}
                      extra={
                        <Button type="link"
                          onClick={() => navigate(`${tenantPath}/crm/company/${firstCompany.id}`)}
                        >
                          {t('View')}
                        </Button>
                      }
                    >
                      <p><strong>{t('Name')}:</strong> {firstCompany.name}</p>
                      {firstCompany.edrpou && <p><strong>{t('EDRPOU')}:</strong> {firstCompany.edrpou}</p>}
                      {firstCompany.addresses?.[0] && (
                        <p>
                          <strong>{t('Address')}:</strong>{' '}
                          {[
                            firstCompany.addresses[0].locationName,
                            firstCompany.addresses[0].city,
                            firstCompany.addresses[0].country,
                          ].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {crmStore.currentCrmEvent?.externalCompanies?.length! > 1 && (
                        <Text type="secondary">
                          {t('AndOther', { count: crmStore.currentCrmEvent!.externalCompanies!.length - 1 })}
                        </Text>
                      )}
                    </Card>
                  )}

                  {!firstContact && !firstCompany && (
                    <Text type="secondary">{t('NoRelatedEntities')}</Text>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Card>
      </Spin>
    </ERPLayout>
  );
});

export default EventDetail;
